import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { FaDollarSign, FaPiggyBank, FaChartBar, FaCloud, FaExclamationTriangle, FaCogs } from 'react-icons/fa';
import { CONST_SITE_LABEL, PLATFORM, STORAGE_KEYS, formatValuesInK, goToPricing, setScopedItem, toWholeString, buildExportFilename, exportCSV, ADD_NEW_ACCOUNT } from '../components/common/Helper';
import DashboardService from '../services/DashboardService';
import { useCallback, useContext, useEffect, useState, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../state/AuthContext';
import DebugService from '../services/DebugService';
import { DateContext } from '../state/DateContext';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import BillingApiService from '../services/BillingApiService';
import K8sApiService from '../services/K8sApiService';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';
import Chart from 'chart.js/auto';
import Card from '../components/common/Card';
import PlanGate from '../components/common/PlanGate';
import usePlanAccess from '../hooks/usePlanAccess';
// import UpgradePlanNote from '../components/common/UpgradePlanNote';
import Table from '../components/common/Table.jsx';
import { BadgeSkeleton, KpiSkeleton, SvcGridSkeleton, ChartGridSkeleton, TableSkeleton, RecsPanelSkeleton } from '../components/optimization/Skeletons';

dayjs.extend(relativeTime);

/* ── Module-level constants ─────────────────────────────────── */
const MONTHS_PER_YEAR    = 12;
const TOP_SERVICES_LIMIT = 6;
const TOP_CLUSTERS_LIMIT = 3;
/** Recommendations shown before the upgrade prompt on a plan without them. */
const RECOMMENDATION_TEASER_COUNT = 2;
const CHART_Y_HEADROOM   = 1.25;
const CHART_Y_MIN        = 0.1;

/* ── Anomaly detection — mirrors backend ANOMALY_COST_MAPPING ── */
const ANOMALY_BASELINE_WINDOW = 14;   // rolling window of historical days
const ANOMALY_MIN_BASELINE    = 7;    // need ≥7 data points before detecting
const ANOMALY_COST_FLOOR      = 0.50; // ignore avg daily cost below $0.50
const ANOMALY_MIN_DELTA       = 0.30; // ignore absolute increase below $0.30
const ANOMALY_Z_LOW           = 1.5;
const ANOMALY_Z_MED           = 2.0;
const ANOMALY_Z_HIGH          = 3.0;
const ANOMALY_LOOKBACK_DAYS   = 30;   // only flag anomalies in the last 30 days

const getSeverityFromZ = (z) => {
  if (z >= ANOMALY_Z_HIGH) return 'High';
  if (z >= ANOMALY_Z_MED)  return 'Med';
  if (z >= ANOMALY_Z_LOW)  return 'Low';
  return null;
};

const ANOMALY_DOT_COLOR = { High: '#e53935', Med: '#fb8c00', Low: '#f9a825' };

const CHART_COLORS = {
  cost:     '#f57c00',
  savings:  '#2196f3',
  unused:   '#2ea66f',
  node:     '#1e88e5',
  storage:  '#43a047',
  lb:       '#f57c00',
  idle:     '#ef4444',
  anomaly:  '#ef4444',
  line:     '#2196f3',
  lineFill: 'rgba(33,150,243,0.14)',
  grid:     '#eef1f7',
  sevHigh:  '#e53935',
  sevMed:   '#fb8c00',
  sevLow:   '#43a047',
  sevUnk:   '#999',
};

/* Keys the recommendation views (panel + savings chart) skip: only FORECAST_COST,
   the spend forecast rather than a saving recommendation — it carries no
   savingCost or displasyAs. Everything else the API returns is listed, ROI
   included. */
const NON_RECOMMENDATION_KEYS = ['FORECAST_COST'];

/* Keys the KPI cards' headline total and percentage skip — excluded from the
   totals only, never from the Recommendations list. ROI is a Savings Plans
   commitment whose saving overlaps EC2 rightsizing, so counting both in one total
   would double-count.
   COMPUTE_OPTIMIZER is included: the total uses the response's own savingCost
   rollup for it, not the Optimizer Recommendations table sum — one or the other,
   never both, or the same money lands in the total twice. */
const EXCLUDED_FROM_TOTALS_KEYS = ['FORECAST_COST', 'ROI'];

/* Display names for services whose serviceName does not read as a label. Compute
   Optimizer reports a comma-joined resource-type list there
   ("IdleLoadBalancer, DynamoDBTable, NatGateway, …"). */
const SERVICE_LABELS = { COMPUTE_OPTIMIZER: 'Compute Optimizer' };

/* Findings the "Unused Resources And Idle Resources" chart covers, and the bar
   colour each gets. Every other finding the optimizer reports (Optimized,
   Overprovisioned, …) is not waste, so it gets no bar. */
const WASTE_FINDING_COLORS = { idle: CHART_COLORS.idle, unused: CHART_COLORS.unused };

/** Monthly savings on an optimizer row, tolerating "$12.34" strings. */
const parseMonthlySavings = (row) => {
  const val = parseFloat(String(row.estimatedMonthlySavings ?? row.savingsUSDPerMonth ?? 0).replace('$', ''));
  return Number.isFinite(val) ? val : 0;
};

const extractResourceName = (row) => {
  const arn = row.functionArn || row.instanceArn || row.volumeArn || row.autoScalingGroupArn || row.autoScalingGroupName || '';
  if (!arn) return row.resourceName ?? '—';
  const afterFunction = arn.split(':function:')[1];
  if (afterFunction) return afterFunction.split(':')[0];
  const afterSlash = arn.split('/').pop();
  if (afterSlash && afterSlash !== arn) return afterSlash;
  const parts = arn.split(':');
  return parts[parts.length - 2] || parts[parts.length - 1] || arn;
};

/* Finding → colored badge palette */
const FINDING_STYLES = {
  optimized:        { bg: '#e6f4ea', fg: '#1e7e34', dot: '#43a047' },
  idle:             { bg: '#fff4e5', fg: '#b26a00', dot: '#fb8c00' },
  underprovisioned: { bg: '#fdecea', fg: '#c62828', dot: '#e53935' },
  overprovisioned:  { bg: '#fdecea', fg: '#c62828', dot: '#e53935' },
  notoptimized:     { bg: '#fdecea', fg: '#c62828', dot: '#e53935' },
  default:          { bg: '#eef1f7', fg: '#5b6472', dot: '#999' },
};

const FindingBadge = ({ finding }) => {
  if (!finding) return <span style={{ color: 'var(--muted, #999)' }}>—</span>;
  const s = FINDING_STYLES[String(finding).toLowerCase()] || FINDING_STYLES.default;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: s.bg, color: s.fg,
      fontSize: 12, fontWeight: 600, lineHeight: 1.4,
      padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {finding}
    </span>
  );
};

/* Human-readable remark/recommendation for a row */
const extractRecommendation = (row) => {
  if (row.findingDescription) return row.findingDescription;
  const codes = Array.isArray(row.findingReasonCodes) ? row.findingReasonCodes.filter(Boolean) : [];
  if (codes.length > 0) return codes.join(', ');
  const finding = String(row.finding ?? '').toLowerCase();
  if (finding === 'optimized') return 'Already optimized — no action needed.';
  if (finding === 'idle') return 'Idle resource — consider deleting to save cost.';
  if (finding === 'underprovisioned') return 'Under-provisioned — consider scaling up.';
  if (finding === 'overprovisioned') return 'Over-provisioned — consider scaling down.';
  return finding ? `${row.finding} — review recommended.` : '—';
};

const SavingsCell = ({ value }) => {
  const amount = parseFloat(value ?? 0) || 0;
  const positive = amount > 0;
  return (
    <span style={{
      fontWeight: 700, fontVariantNumeric: 'tabular-nums',
      color: positive ? 'var(--green, #1e7e34)' : 'var(--muted, #8a929e)',
    }}>
      ${amount.toFixed(2)}
    </span>
  );
};

const OPTIMIZER_COLUMNS = [
  { key: 'resourceType', label: 'Resource Type', render: (row) => (
      <span style={{ fontWeight: 600, color: 'var(--text, #222)' }}>{row.resourceType ?? '—'}</span>
    ) },
  { key: 'resourceName', label: 'Resource Name', render: (row) => extractResourceName(row) },
  { key: 'accountId',    label: 'Account',       render: (row) => (
      <span style={{ color: 'var(--muted, #5b6472)', fontVariantNumeric: 'tabular-nums' }}>{row.accountId ?? '—'}</span>
    ) },
  { key: 'region',       label: 'Region',        render: (row) => (
      <span style={{
        background: '#eef4ff', color: '#2b5bb5', fontSize: 12, fontWeight: 600,
        padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap',
      }}>{row.region ?? '—'}</span>
    ) },
  { key: 'finding',      label: 'Finding',        render: (row) => <FindingBadge finding={row.finding} /> },
  { key: 'recommendation', label: 'Recommendation', minWidth: 260, render: (row) => (
      <span style={{ color: 'var(--text, #444)', fontSize: 13 }}>{extractRecommendation(row)}</span>
    ) },
  { key: 'savingsUSDPerMonth', label: 'Saving / mo', className: 'num', render: (row) => <SavingsCell value={row.savingsUSDPerMonth} /> },
];

const ImpactBadge = ({ impact }) => {
  if (!impact) return <span>—</span>;
  const colors = {
    High: { bg: '#fdecea', fg: '#c62828', dot: '#e53935' },
    Medium: { bg: '#fff4e5', fg: '#b26a00', dot: '#fb8c00' },
    Low: { bg: '#e6f4ea', fg: '#1e7e34', dot: '#43a047' },
  };
  const s = colors[impact] || { bg: '#eef1f7', fg: '#5b6472', dot: '#999' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: s.bg, color: s.fg,
      fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap'
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {impact}
    </span>
  );
};

const AZURE_ADVISORY_COLUMNS = [
  { key: 'category', label: 'Category', render: (row) => row.category || row.recommendationSubCategory || 'Cost' },
  { key: 'impact', label: 'Impact', render: (row) => <ImpactBadge impact={row.impact} /> },
  { key: 'solution', label: 'Recommendation', minWidth: 260, render: (row) => (
      <span style={{ color: 'var(--text, #444)', fontSize: 13 }}>{row.solution || row.shortDescription?.solution || '—'}</span>
    ) },
  { key: 'sku', label: 'SKU / Scope', render: (row) => row.sku || row.displaySKU || row.scope || '—' },
  { key: 'region', label: 'Region', render: (row) => (
      <span style={{ background: '#eef4ff', color: '#2b5bb5', fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>
        {row.region || '—'}
      </span>
    ) },
  { key: 'savingsAmount', label: 'Saving / mo', className: 'num', render: (row) => (
      <SavingsCell value={row.savingsAmount || (row.annualSavingsAmount ? (parseFloat(row.annualSavingsAmount) / 12).toFixed(2) : 0)} />
    ) },
];

const AWS_SVC_CARDS = [
  { key: 'EC2',          label: 'EC2 Recommendations',  costKey: 'currentCost'    },
  { key: 'EBS',          label: 'EBS Recommendations',  costKey: 'currentCost'    },
  { key: 'RDS',          label: 'RDS Recommendations',  costKey: 'currentCost'    },
  { key: 'ELASTIC_IP',   label: 'Elastic IP Savings',   costKey: 'currentCost'    },
  { key: 'EBS_SNAPSHOT', label: 'EBS Snapshot Savings', costKey: 'totalCostValue' },
];

const AZURE_SVC_CARDS = [
  { key: 'VM',           label: 'VM Recommendations',        costKey: 'currentCost' },
  { key: 'MANAGED_DISK', label: 'Managed Disk',              costKey: 'currentCost' },
  { key: 'PUBLIC_IP',    label: 'Public IP Recommendations', costKey: 'currentCost' },
  // { key: 'MANAGED_DISK_SNAPSHOT', label: 'Managed Disk Snapshot', costKey: 'currentCost' },
];

/* ── K8s cost allocation doughnut ────────────────────────────── */
const K8sCostDonutChart = ({ nodeCost, storageCost, lbCost, idleCost }) => {
  const canvasRef = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    const vals = [nodeCost, storageCost, lbCost, idleCost];
    if (!vals.some(v => v > 0) || !canvasRef.current) return;
    if (instanceRef.current) { instanceRef.current.destroy(); instanceRef.current = null; }
    instanceRef.current = new Chart(canvasRef.current.getContext('2d'), {
      type: 'doughnut',
      data: {
        labels: ['Node', 'Storage', 'Load Balancer', 'Idle'],
        datasets: [{ data: vals, backgroundColor: [CHART_COLORS.node, CHART_COLORS.storage, CHART_COLORS.lb, CHART_COLORS.idle], borderWidth: 2, borderColor: '#fff' }],
      },
      options: {
        maintainAspectRatio: false,
        cutout: '68%',
        plugins: { legend: { display: false }, tooltip: { callbacks: { label: c => `$${c.parsed.toFixed(2)}` } } },
      },
    });
    return () => { if (instanceRef.current) { instanceRef.current.destroy(); instanceRef.current = null; } };
  }, [nodeCost, storageCost, lbCost, idleCost]);

  return <canvas ref={canvasRef} />;
};

/* ── Service mini-bar chart ──────────────────────────────────── */
const ServiceMiniBarChart = ({ cost, saving }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    const costK = parseFloat(((cost || 0) / 1000).toFixed(2));
    const savK  = parseFloat(((saving || 0) / 1000).toFixed(2));
    const maxK  = Math.max(parseFloat((Math.max(costK, savK) * CHART_Y_HEADROOM).toFixed(2)), CHART_Y_MIN);
    const ctx = canvasRef.current.getContext('2d');
    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Cost', 'Savings'],
        datasets: [{ data: [costK, savK], backgroundColor: [CHART_COLORS.cost, CHART_COLORS.savings], borderRadius: 4, barPercentage: 0.55 }],
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: c => `$${c.parsed.y.toFixed(2)}K` } },
        },
        scales: {
          y: { beginAtZero: true, max: maxK, ticks: { font: { size: 9 }, callback: v => `$${v}K` }, grid: { color: CHART_COLORS.grid } },
          x: { ticks: { font: { size: 10 } }, grid: { display: false } },
        },
      },
    });
    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [cost, saving]);

  return <canvas ref={canvasRef} />;
};

/* ── Horizontal bar chart card ───────────────────────────────── */
const HorizontalBarChart = ({ labels, fullLabels, data, color, title, subTitle }) => {
  const canvasRef = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    if (!labels?.length || !data?.length) return;
    if (instanceRef.current) instanceRef.current.destroy();
    const ctx = canvasRef.current.getContext('2d');
    instanceRef.current = new Chart(ctx, {
      type: 'bar',
      data: { labels, datasets: [{ data, backgroundColor: color, borderRadius: 4, barPercentage: 0.6 }] },
      options: {
        indexAxis: 'y',
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: {
            title: (items) => fullLabels ? (fullLabels[items[0].dataIndex] ?? items[0].label) : items[0].label,
            label: c => `$${c.parsed.x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          }},
        },
        scales: {
          x: { ticks: { font: { size: 9 }, callback: v => `$${(v / 1000).toFixed(1)}K` }, grid: { color: CHART_COLORS.grid } },
          y: { ticks: { font: { size: 10 } }, grid: { display: false } },
        },
      },
    });
    return () => { if (instanceRef.current) instanceRef.current.destroy(); };
  }, [labels, fullLabels, data, color]);

  return (
    <Card title={title} subtitle={subTitle} className="first">
      <div style={{ height: '220px', position: 'relative', marginTop: '10px' }}>
        <canvas ref={canvasRef} />
      </div>
    </Card>
  );
};

/* ── Daily cost trend line chart ─────────────────────────────── */
const DailyCostTrendChart = ({ labels, data, platformLabel }) => {
  const canvasRef = useRef(null);
  const instanceRef = useRef(null);

  /* Rolling-window z-score detection — same algorithm as backend detectCostAnomalies.
     Only flags anomalies in the last ANOMALY_LOOKBACK_DAYS days of the series. */
  const anomalies = useMemo(() => {
    if (data.length < ANOMALY_MIN_BASELINE + 1) return [];
    const result = [];
    const lookbackStart = Math.max(ANOMALY_MIN_BASELINE, data.length - ANOMALY_LOOKBACK_DAYS);
    for (let i = lookbackStart; i < data.length; i++) {
      // Baseline = up to ANOMALY_BASELINE_WINDOW days before index i
      const windowStart = Math.max(0, i - ANOMALY_BASELINE_WINDOW);
      const baseline = data.slice(windowStart, i);
      if (baseline.length < ANOMALY_MIN_BASELINE) continue;

      const avg = baseline.reduce((a, b) => a + b, 0) / baseline.length;
      if (avg < ANOMALY_COST_FLOOR) continue;

      const sd = Math.sqrt(baseline.reduce((s, v) => s + (v - avg) ** 2, 0) / baseline.length);
      const currentCost = data[i];
      const absoluteDelta = Math.max(0, currentCost - avg);
      if (absoluteDelta < ANOMALY_MIN_DELTA) continue;
      if (sd === 0) continue;

      const z = (currentCost - avg) / sd;
      if (!Number.isFinite(z)) continue;

      const severity = getSeverityFromZ(z);
      if (!severity) continue;

      result.push({ index: i, severity, z });
    }
    return result;
  }, [data]);

  /* Fast index → severity lookup for chart rendering */
  const anomalyMap = useMemo(() => {
    const m = {};
    anomalies.forEach(a => { m[a.index] = a; });
    return m;
  }, [anomalies]);

  useEffect(() => {
    if (!labels?.length || !data?.length) return;
    if (instanceRef.current) instanceRef.current.destroy();
    const ctx = canvasRef.current.getContext('2d');
    instanceRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          data,
          borderColor: CHART_COLORS.line, backgroundColor: CHART_COLORS.lineFill,
          fill: true, tension: 0.3, borderWidth: 2,
          pointRadius: data.map((_, i) => anomalyMap[i] ? 7 : 2),
          pointBackgroundColor: data.map((_, i) =>
            anomalyMap[i] ? (ANOMALY_DOT_COLOR[anomalyMap[i].severity] ?? CHART_COLORS.anomaly) : CHART_COLORS.line
          ),
          pointBorderColor: '#fff', pointBorderWidth: 2,
        }],
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: c => {
            const a = anomalyMap[c.dataIndex];
            return `$${c.parsed.y.toFixed(2)}${a ? ` ⚠ ${a.severity} anomaly (z=${a.z.toFixed(1)})` : ''}`;
          }}},
        },
        scales: {
          y: { beginAtZero: false, ticks: { font: { size: 10 }, callback: v => `$${v.toLocaleString()}` }, grid: { color: CHART_COLORS.grid } },
          x: { ticks: { font: { size: 9 }, autoSkip: true, maxRotation: 0 }, grid: { display: false } },
        },
      },
    });
    return () => { if (instanceRef.current) instanceRef.current.destroy(); };
  }, [labels, data, anomalyMap]);

  /* Badge breakdown by severity */
  const sevCounts = useMemo(() => {
    const c = { High: 0, Med: 0, Low: 0 };
    anomalies.forEach(a => { c[a.severity] = (c[a.severity] || 0) + 1; });
    return c;
  }, [anomalies]);

  const badgeParts = ['High', 'Med', 'Low']
    .filter(s => sevCounts[s] > 0)
    .map(s => `${sevCounts[s]} ${s}`);

  return (
    <Card
      title={<>
        Daily Cost Trend
        {anomalies.length > 0 && (
          <span className="anom-badge" style={{ marginLeft: 8 }}>
            ⚠ {anomalies.length} {anomalies.length === 1 ? 'anomaly' : 'anomalies'} · {badgeParts.join(' · ')}
          </span>
        )}
      </>}
      subtitle={`Daily ${platformLabel} spend · last ${labels.length} days · anomaly detection: last ${ANOMALY_LOOKBACK_DAYS} days`}
      className="first"
    >
      <div style={{ height: '260px', position: 'relative', marginTop: '10px' }}>
        <canvas ref={canvasRef} />
      </div>
    </Card>
  );
};

const shortenLabel = (label) => {
  if (!label) return label;
  const expanded = label.replace(/([A-Z])/g, ' $1').trim();
  const words = expanded.split(' ').filter(Boolean);
  const firstLower = words[0].toLowerCase();
  const filtered = (firstLower === 'amazon' || firstLower === 'aws') ? words.slice(1) : words;
  return filtered.map(w => w[0]?.toUpperCase()).join('');
};

/* Service keys whose optimization page is not named after the key itself. */
const OPTIMIZATION_ROUTE_OVERRIDES = {
  VM: CONST_ROUTE_PATH.OPTIMIZATION.VIRTUAL_MACHINE,
  MANAGED_DISK: CONST_ROUTE_PATH.OPTIMIZATION.MANAGED_DISKS,
  MANAGED_DISK_SNAPSHOT: CONST_ROUTE_PATH.OPTIMIZATION.MANAGED_DISKS_SNAPSHOT,
  MANAGED_DISK_SNAPSHOTS: CONST_ROUTE_PATH.OPTIMIZATION.MANAGED_DISKS_SNAPSHOT,
  EKS: CONST_ROUTE_PATH.K8S.CONNECT_K8S_CLUSTER,
  POSTGRESQL: CONST_ROUTE_PATH.OPTIMIZATION.AZURE_DB_POSTGRESSQL,
};

/** Optimization page for a service key, or null when it has no drill-down page. */
const optimizationRouteFor = (key) =>
  OPTIMIZATION_ROUTE_OVERRIDES[key] ?? CONST_ROUTE_PATH.OPTIMIZATION[key] ?? null;

const SVC_TAG_CLASS = {
  EC2: 'ec2', EBS: 'ebs', RDS: 'rds', ELASTIC_IP: 'eip',
  EBS_SNAPSHOT: 'snap', RDS_SNAPSHOT: 'snap', ROI: 'roi', EKS: 'eks', COMPUTE_OPTIMIZER: 'co',
  VM: 'vm', MANAGED_DISK: 'disk', MANAGED_DISK_SNAPSHOT: 'snap', PUBLIC_IP: 'pip', POSTGRESQL: 'pg',
};

/* ── Main Dashboard Page ─────────────────────────────────────── */
const Dashboard = () => {
  const [allServicesData, setAllServicesData] = useState({});
  /* Raw dollars, not pre-formatted $K strings — formatting happens once, in
     `summary`, so nothing is rounded twice on the way to the screen. */
  const [totalMonthlyCost, setTotalMonthlyCost] = useState(0);
  const [totalMonthlySaving, setTotalMonthlySaving] = useState(0);
  const [VMMonthlySaving, setVMMonthlySaving] = useState('0');
  const [ManagedDiskMonthlySaving, setManagedDiskMonthlySaving] = useState('0');
  const [PublicIPMonthlySaving, setPublicIPMonthlySaving] = useState('0');

  const [isLoading, setIsLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const datePillRef = useRef(null);
  const lastFetchParamsRef = useRef(null);
  const [pillMounted, setPillMounted] = useState(false);
  useLayoutEffect(() => { if (datePillRef.current) setPillMounted(true); }, []);
  const { isSubscriptionActive, selectedPlatform, selectedAccount, loadingUserDetails, accountList } = useAuth();
  const { isTrial: isTrialPlan, planName } = usePlanAccess();
  const { selectedDate, setSelectedDate } = useContext(DateContext);
  const { getScopedItem } = useScopedLocalStorage();
  const navigate = useNavigate();

  const [costExplorerColumnList, setCostExplorerColumnList] = useState([]);
  const [costExplorerData, setCostExplorerData] = useState([]);
  const [costExplorerApiData, setCostExplorerApiData] = useState([]);
  const [advisoryColumnList, setAdvisoryColumnList] = useState([]);
  const [advisoryApiData, setAdvisoryApiData] = useState([]);
  const [allOptimizerColumnList, setAllOptimizerColumnList] = useState([]);
  const [allOptimizerData, setAllOptimizerData] = useState([]);
  const [anomalyColumnList, setAnomalyColumnList] = useState([]);
  const [anomalyData, setAnomalyData] = useState([]);
  const [isOptimizerLoading, setIsOptimizerLoading] = useState(true);
  const [k8sData, setK8sData] = useState(null);
  const [isK8sLoading, setIsK8sLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [lastK8sRefreshed, setLastK8sRefreshed] = useState(null);

  const [dataFilters, setDataFilters] = useState({
    startMonthDate: dayjs().subtract(30, 'days'),
    endMonthDate: dayjs(),
    granularity: 'DAILY',
    dimension: selectedPlatform === PLATFORM.AWS ? 'SERVICE' : 'SERVICE_NAME',
    filter: '',
  });

  /* ── Derived chart data ────────────────────────────────────── */
  /* Built solely from Optimizer Recommendations — the same rows the optimizer
     table below renders — grouped by the finding the optimizer reported. No
     fallback to the ALL_SERVICES recommendations: when the optimizer reports no
     idle/unused savings the chart has nothing to say, so it renders nothing. */
  const savingsByResourceType = useMemo(() => {
    const totals = new Map();
    allOptimizerData.forEach(row => {
      const finding = String(row.finding ?? '').trim();
      if (!WASTE_FINDING_COLORS[finding.toLowerCase()]) return;
      const val = parseMonthlySavings(row);
      if (val <= 0) return;
      totals.set(finding, (totals.get(finding) ?? 0) + val);
    });
    const entries = [...totals].sort((a, b) => b[1] - a[1]);
    return {
      labels: entries.map(([finding]) => finding),
      data:   entries.map(([, v]) => parseFloat(v.toFixed(2))),
      colors: entries.map(([finding]) => WASTE_FINDING_COLORS[finding.toLowerCase()]),
    };
  }, [allOptimizerData]);

  const costByService = useMemo(() => {
    const apiData = costExplorerApiData?.data ?? [];
    const sorted = [...apiData].filter(s => s.totalCostValue > 0).sort((a, b) => b.totalCostValue - a.totalCostValue).slice(0, TOP_SERVICES_LIMIT);
    return { labels: sorted.map(s => s.service), data: sorted.map(s => parseFloat(s.totalCostValue.toFixed(2))) };
  }, [costExplorerApiData]);

  /* Mirrors the KPI cards — same source, same exclusions — so the bars always sum
     to the headline total. Labels come from SERVICE_LABELS where serviceName does
     not read as one (Compute Optimizer reports a comma-joined resource-type list
     there, unusable as an axis label). */
  const savingsByRecommendation = useMemo(() => {
    const entries = Object.entries(allServicesData)
      .filter(([key, val]) => !EXCLUDED_FROM_TOTALS_KEYS.includes(key) && (val.savingCost ?? 0) > 0)
      .map(([key, v]) => [SERVICE_LABELS[key] ?? v.serviceName, v.savingCost])
      .sort((a, b) => b[1] - a[1]);
    return {
      labels: entries.map(([label]) => label),
      data:   entries.map(([, saving]) => parseFloat(saving.toFixed(2))),
    };
  }, [allServicesData]);

  const k8sMetrics = useMemo(() => {
    if (!k8sData) return null;
    const clusters = k8sData.clusterDetailsList ?? [];
    const totalCost = k8sData.totalClusterCost ?? 0;
    const idleCost = k8sData.totalIdleClusterCost ?? 0;
    const storageCost = (k8sData.totalClusterEBSCost ?? 0) + (k8sData.totalClusterDiskCost ?? 0);
    const lbCost = clusters.reduce((s, c) => s + (parseFloat(c.clusterLoadBalancerCost) || 0), 0);
    const nodeCost = Math.max(0, totalCost - idleCost - storageCost - lbCost);
    const totalCPU = k8sData.totalClusterCPU ?? 0;
    const usedCPU = k8sData.totalClusterUsedCPU ?? 0;
    const avgUtilization = totalCPU > 0 ? ((usedCPU / totalCPU) * 100).toFixed(1) : null;
    const topClusters = [...clusters].sort((a, b) => (b.clusterTotalCost ?? 0) - (a.clusterTotalCost ?? 0)).slice(0, TOP_CLUSTERS_LIMIT);
    return {
      totalCost, idleCost, storageCost, lbCost, nodeCost,
      activeClusters: k8sData.totalClusters ?? clusters.length,
      totalNodes: k8sData.totalClusterNodes ?? 0,
      avgUtilization,
      topClusters,
      hasData: totalCost > 0 || clusters.length > 0,
    };
  }, [k8sData]);

  const dailyCostTrend = useMemo(() => {
    const rawApiData = costExplorerApiData?.data ?? (Array.isArray(costExplorerApiData) ? costExplorerApiData : []);
    if (!rawApiData.length) return { labels: [], data: [] };
    const dateMap = {};
    rawApiData.forEach(svc => {
      const datesObj = svc.costDates || svc.dates || svc.dailyCosts || {};
      Object.entries(datesObj).forEach(([date, cost]) => {
        if (date && date !== 'totalCostValue' && date !== 'total') {
          dateMap[date] = (dateMap[date] ?? 0) + (parseFloat(cost) || 0);
        }
      });
    });
    const keys = Object.keys(dateMap);
    if (!keys.length) return { labels: [], data: [] };
    const sortedDates = keys.sort((a, b) => {
      const dA = dayjs(a).isValid() ? dayjs(a).valueOf() : dayjs(a, 'DD MMM YYYY').valueOf();
      const dB = dayjs(b).isValid() ? dayjs(b).valueOf() : dayjs(b, 'DD MMM YYYY').valueOf();
      return dA - dB;
    });
    return {
      labels: sortedDates.map(d => dayjs(d).isValid() ? dayjs(d).format('DD MMM') : d),
      data: sortedDates.map(d => parseFloat(dateMap[d].toFixed(2)))
    };
  }, [costExplorerApiData]);

  const activeChartCount = useMemo(() => [
    selectedPlatform === PLATFORM.AWS && savingsByResourceType.labels.length > 0,
    costByService.labels.length > 0,
    savingsByRecommendation.labels.length > 0,
  ].filter(Boolean).length, [selectedPlatform, savingsByResourceType, costByService, savingsByRecommendation]);

  /* Card values, all in $K. The saving is the per-service total straight from the
     response — Compute Optimizer included via its own savingCost — and the
     percentage is derived from that same figure so the amount and the percentage
     can never disagree. */
  const summary = useMemo(() => {
    const cost   = totalMonthlyCost;
    const saving = totalMonthlySaving;
    return [
      { value: formatValuesInK(cost),   annual: formatValuesInK(cost * MONTHS_PER_YEAR) },
      { value: formatValuesInK(saving), annual: formatValuesInK(saving * MONTHS_PER_YEAR) },
      { value: cost > 0 ? ((saving / cost) * 100).toFixed(2) : '0' },
    ];
  }, [totalMonthlyCost, totalMonthlySaving]);

  const currentAccountLabel = useMemo(() => {
    const acct = accountList?.find(a => a.accountId === selectedAccount);
    return acct ? `${acct.accountId} — ${acct.accountAliase}` : selectedAccount || '—';
  }, [accountList, selectedAccount]);

  /* Every actionable recommendation the API returned, highest saving first. A
     service with nothing to save is not a recommendation, so it is left out
     rather than listed as $0.00. */
  const recommendationItems = useMemo(
    () => Object.entries(allServicesData)
      .filter(([key, v]) => !NON_RECOMMENDATION_KEYS.includes(key) && (v?.savingCost ?? 0) > 0)
      .sort((a, b) => b[1].savingCost - a[1].savingCost),
    [allServicesData]
  );

  const hasRecs = recommendationItems.length > 0;

  const [isRefreshing, setIsRefreshing] = useState(false);

  // Plan cap messages from the API, per dashboard section - each is its own
  // request with its own count. Empty string when nothing was trimmed.
  const [upgradeMessages, setUpgradeMessages] = useState({});
  /** Largest per-source count the plan cap withheld, for the upgrade prompt. */
  const [remainingRecommendations, setRemainingRecommendations] = useState(0);
  // const readUpgradeMessage = (source) => {
  //   setUpgradeMessages((prev) => ({ ...prev, [source]: DashboardService.getUpgradePlanMessage(source) }));
  //   setRemainingRecommendations(DashboardService.getMaxRemainingResourcesCount());
  // };

  /* ── API calls ─────────────────────────────────────────────── */
  const fetchData = async (isForceRefresh = false) => {
    setIsLoading(true);
    if (selectedPlatform === PLATFORM.AZURE) {
      await DashboardService.fetchAzureData(selectedAccount, selectedDate ?? '', isForceRefresh);
      setVMMonthlySaving(formatValuesInK(DashboardService.getVMMonthlySaving()));
      setManagedDiskMonthlySaving(formatValuesInK(DashboardService.getManagedDiskMonthlySaving()));
      setPublicIPMonthlySaving(formatValuesInK(DashboardService.getPublicIPMonthlySaving()));
    } else {
      await DashboardService.fetchData(selectedAccount, selectedDate ?? '', isForceRefresh);
    }

    /* KPI card totals, derived here for both platforms from the one response, so
       AWS and Azure cannot drift and ROI is excluded from both consistently. */
    const apiData = DashboardService.getAPIData();
    const forecastCost = parseFloat(DashboardService.getTotalMonthlyCost()) || 0;
    const totalSaving = Object.entries(apiData)
      .filter(([key]) => !EXCLUDED_FROM_TOTALS_KEYS.includes(key))
      .reduce((sum, [, v]) => sum + (v?.savingCost ?? 0), 0);

    setAllServicesData(apiData);
    setTotalMonthlyCost(forecastCost);
    setTotalMonthlySaving(totalSaving);

    // readUpgradeMessage('allServices');
    setIsLoading(false);
    setLastRefreshed(dayjs());
  };

  const fetchAdvisoryApiData = async (isForceRefresh = false) => {
    if (selectedPlatform !== PLATFORM.AZURE) return;
    await DashboardService.fetchAzureAdvisoryData(selectedAccount, selectedDate ?? '', isForceRefresh);
    const advisoryData = DashboardService.getAdvisoryAPIData();
    const list = Array.isArray(advisoryData) ? advisoryData : (advisoryData?.data ?? []);
    setAdvisoryApiData(list);
    // readUpgradeMessage('advisory');
  };

  const fetchComputeOptimizerApiData = async (isForceRefresh = false) => {
    if (selectedPlatform !== PLATFORM.AWS) { setIsOptimizerLoading(false); return; }
    setIsOptimizerLoading(true);
    try {
      await DashboardService.fetchComputeOptimizerData(selectedAccount, selectedDate ?? '', isForceRefresh);
      const resp = DashboardService.getComputeOptimizerAPIData() ?? [];
      const rawData = Array.isArray(resp) ? resp : (resp?.data ?? []);
      setAllOptimizerColumnList(OPTIMIZER_COLUMNS);
      setAllOptimizerData(rawData);
      // readUpgradeMessage('optimizer');
    } catch (err) {
      DebugService.error('Dashboard: optimizer fetch failed', err);
    } finally {
      setIsOptimizerLoading(false);
    }
  };

  const fetchCostAnomalyData = async (isForceRefresh = false) => {
    if (selectedPlatform !== PLATFORM.AWS && selectedPlatform !== PLATFORM.AZURE) return;
    try {
      if (selectedPlatform === PLATFORM.AZURE) {
        await DashboardService.fetchAzureCostAnomalyData(selectedAccount, selectedDate ?? '', isForceRefresh);
      } else {
        await DashboardService.fetchCostAnomalyData(selectedAccount, selectedDate ?? '', isForceRefresh);
      }
      const rawData = DashboardService.getCostAnomalyAPIData() ?? [];
      // readUpgradeMessage('anomaly');
      const report = rawData?.[0]?.costIncreaseReport ?? [];
      const sevColor = { High: '#e53935', Med: '#fb8c00', Low: '#43a047' };
      setAnomalyColumnList([
        { key: 'severity', label: 'Severity', render: (row) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: sevColor[row.severity] ?? '#999', flexShrink: 0 }} />
            {row.severity}
          </span>
        )},
        { key: 'date',             label: 'Date' },
        { key: 'service',          label: 'Service' },
        { key: 'region',           label: 'Region' },
        { key: 'currentCost',      label: 'Current ($)' },
        { key: 'previousCost',     label: 'Previous ($)' },
        { key: 'increaseCost',     label: 'Increase ($)' },
        { key: 'percentageChange', label: 'Change', render: (row) => {
          const pct = parseFloat(row.percentageChange) || 0;
          return <span className={`delta-pill ${pct >= 0 ? 'up' : 'down'}`}>{pct >= 0 ? '↑' : '↓'} {Math.abs(pct)}%</span>;
        }},
      ]);
      setAnomalyData(report.map((item, i) => ({
        id: item.id ?? i, severity: item.severity, date: item.date, service: item.service,
        region: item.region, currentCost: item.currentCost, previousCost: item.previousCost,
        increaseCost: item.increaseCost, percentageChange: item.percentageChange,
      })));
    } catch (err) {
      DebugService.error('Dashboard: anomaly fetch failed', err);
    }
  };

  const fetchK8sData = async (isForceRefresh = false) => {
    setIsK8sLoading(true);
    try {
      const platform = selectedPlatform === PLATFORM.AZURE ? 'aks' : 'eks';
      const resp = await K8sApiService.getClusterData(platform, {
        userAccountId: selectedAccount ?? '',
        isForceRefresh,
      });
      DebugService.log('[Dashboard] K8s cluster data:', resp);
      setK8sData(resp?.data ?? null);
      setLastK8sRefreshed(dayjs());
    } catch (err) {
      DebugService.error('[Dashboard] K8s fetch failed', err);
    } finally {
      setIsK8sLoading(false);
    }
  };

  const fetchCostExplorerData = (isForceRefresh = false) => {
    setCostExplorerData([]);
    setCostExplorerColumnList([]);
    const req = {
      ...dataFilters,
      userAccountId: selectedAccount,
      type: 'COST_EXPLORER',
      startMonthDate: dataFilters.startMonthDate?.format('YYYY-MM-DD'),
      endMonthDate: dataFilters.endMonthDate?.format('YYYY-MM-DD'),
    };
    if (isForceRefresh) {
      req['isForceRefresh'] = true;
    }
    delete req['filter'];

    const call = selectedPlatform === PLATFORM.AZURE
      ? BillingApiService.getAzureCostExplorerData(req)
      : selectedPlatform === PLATFORM.AWS
        ? BillingApiService.getCostExplorerData(req)
        : null;

    if (!call) return Promise.resolve();
    return call.then(res => {
      setCostExplorerApiData(res);
      const apiData = res?.data ?? [];
      if (apiData.length > 0 && apiData[0]['costDates']) {
        const keys = [];
        let cur = dataFilters.startMonthDate.startOf('day');
        const last = dataFilters.endMonthDate.startOf('day');
        while (cur.isBefore(last) || cur.isSame(last, 'day')) {
          keys.push(cur.format('YYYY-MM-DD'));
          cur = cur.add(1, 'day');
        }
        if (keys.length > 0) {
          const totalCellStyle = { fontWeight: 700, color: 'var(--brand, #2196f3)' };
          setCostExplorerColumnList([
            {
              key: 'service',
              label: toWholeString(dataFilters.dimension ?? 'Service'),
              render: (row) => row.__isTotal
                ? <span style={{ fontWeight: 700 }}>Total (all services)</span>
                : (row.service || row.SERVICE || row.serviceName || row.id || '—'),
            },
            {
              key: 'totalCostValue',
              label: 'Total Cost',
              render: (row) => {
                const text = `$${parseFloat(row.totalCostValue ?? 0).toFixed(2)}`;
                return row.__isTotal ? <span style={totalCellStyle}>{text}</span> : text;
              },
            },
            ...keys.map(d => ({
              key: d,
              label: dayjs(d).format('DD MMM YYYY'),
              render: (row) => {
                const v = row.costDates?.[dayjs(d).format('DD MMM YYYY')];
                const text = v != null ? `$${parseFloat(v).toFixed(2)}` : '$0.00';
                return row.__isTotal ? <span style={totalCellStyle}>{text}</span> : text;
              },
            })),
          ]);
        }
        const sortedRows = apiData
          .sort((a, b) => b.totalCostValue - a.totalCostValue)
          .map(s => ({ ...s, id: s.service ?? s.SERVICE ?? s.serviceName }));
        // Grand-total row summing every service (overall total + per-day totals)
        const totalRow = {
          id: '__total__',
          __isTotal: true,
          totalCostValue: sortedRows.reduce((sum, r) => sum + (parseFloat(r.totalCostValue) || 0), 0),
          costDates: keys.reduce((acc, d) => {
            const label = dayjs(d).format('DD MMM YYYY');
            acc[label] = sortedRows.reduce((sum, r) => sum + (parseFloat(r.costDates?.[label]) || 0), 0);
            return acc;
          }, {}),
        };
        setCostExplorerData(sortedRows.length > 0 ? [totalRow, ...sortedRows] : sortedRows);
      }
    }).catch(err => DebugService.error('Dashboard: cost explorer fetch failed', err));
  };

  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | Dashboard`;
  }, []);

  useEffect(() => {
    const daysBefore = selectedDate ? selectedDate.subtract(30, 'days') : dayjs().subtract(30, 'days');
    setDataFilters({
      startMonthDate: daysBefore,
      endMonthDate: selectedDate ?? dayjs(),
      granularity: 'DAILY',
      dimension: selectedPlatform === PLATFORM.AWS ? 'SERVICE' : 'SERVICE_NAME',
      filter: '',
    });
    if (selectedPlatform !== PLATFORM.AZURE) {
      setVMMonthlySaving('0');
      setManagedDiskMonthlySaving('0');
      setPublicIPMonthlySaving('0');
    }
  }, [selectedDate, selectedAccount, selectedPlatform]);

  useEffect(() => {
    // Skip until AuthContext has resolved a real account (ADD_NEW_ACCOUNT is the placeholder initial value).
    if (!selectedAccount || selectedAccount === ADD_NEW_ACCOUNT) return;
    // Wait for fetchUserData() to fully complete — it saves userDetails to localStorage,
    // which RecommendationApiService needs to build the userId in the request body.
    // Without this guard, the first login fires recommendations before userId is available → 502.
    if (loadingUserDetails !== false) return;
    // Deduplicate: skip if account/platform/date haven't changed — guards against
    // StrictMode double-mount and back-to-back renders with identical params.
    const fetchParams = `${selectedAccount}_${selectedPlatform}_${dataFilters.startMonthDate?.format('YYYY-MM-DD')}`;
    if (lastFetchParamsRef.current === fetchParams) return;
    lastFetchParamsRef.current = fetchParams;
    setIsPageLoading(true);
    Promise.all([
      fetchData(),
      fetchCostExplorerData() ?? Promise.resolve(),
      fetchAdvisoryApiData(),
      fetchComputeOptimizerApiData(),
      fetchCostAnomalyData(),
      fetchK8sData(),
    ]).finally(() => setIsPageLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFilters.startMonthDate, loadingUserDetails]);

  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      allServicesData, advisoryApiData, costExplorerApiData, computeOptimizerApiData: allOptimizerData,
    });
  }, [allServicesData, advisoryApiData, costExplorerApiData, allOptimizerData]);

  const handleRefreshDashboard = async () => {
    if (isRefreshing || isLoading) return;
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchData(true),
        fetchCostExplorerData(true) ?? Promise.resolve(),
        fetchAdvisoryApiData(true),
        fetchComputeOptimizerApiData(true),
        fetchCostAnomalyData(true),
        fetchK8sData(true),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const navigateToOptimization = useCallback((key) => {
    const path = optimizationRouteFor(key);
    if (path) { setScopedItem(STORAGE_KEYS.CURRENT_ROUTE, path); navigate(path); }
  }, []);

  /* ── Full-page shimmer while all APIs are in-flight ────────── */
  if (isPageLoading) {
    return (
      <>
        {/* Header */}
        <div className="page-head" style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="shimmer-bg" style={{ width: 110, height: 22, borderRadius: 5 }} />
              <div className="shimmer-bg shimmer-badge" />
            </div>
            <div className="shimmer-bg" style={{ width: 260, height: 12, borderRadius: 4 }} />
          </div>
          <div className="shimmer-bg" style={{ width: 220, height: 42, borderRadius: 9 }} />
        </div>

        {/* KPI tiles */}
        <KpiSkeleton count={3} cols={3} />

        {/* Service cards */}
        <SvcGridSkeleton count={5} />

        {/* 3 charts */}
        <ChartGridSkeleton cols={3} />

        {/* Daily cost trend */}
        <div style={{ background: '#fff', border: '1px solid #e9ecf2', borderRadius: 11, padding: '16px 18px', marginBottom: 14 }}>
          <div className="shimmer-bg" style={{ width: 180, height: 13, marginBottom: 6 }} />
          <div className="shimmer-bg" style={{ width: 120, height: 10, marginBottom: 18 }} />
          <div className="shimmer-bg" style={{ width: '100%', height: 140, borderRadius: 8 }} />
        </div>

        {/* Optimizer table */}
        <TableSkeleton rows={5} cols={6} />

        {/* Bottom grid: cost breakup + recommendations */}
        <div className="grid-bottom">
          <TableSkeleton rows={6} cols={4} />
          <RecsPanelSkeleton />
        </div>

        {/* Cost anomaly table */}
        <TableSkeleton rows={4} cols={5} />
      </>
    );
  }

  /**
   * Single-line upgrade prompt. Null on plans with nothing left to sell, so the
   * banner disappears rather than nagging a user who has no next step.
   */
  // const upgradePrompt = buildPlanUpgradePrompt({
  //   isTrial: isTrialPlan,
  //   planName,
  //   remainingRecommendations,
  // });

  // const handleUpgradePromptClick = () => {
  //   // Standard has no sellable tier above it - Enterprise is quote-only, so the
  //   // next step is a conversation rather than a checkout.
  //   if (upgradePrompt?.target === 'appointment') {
  //     navigate(CONST_ROUTE_PATH.SCHEDULE_MEETING);
  //     return;
  //   }
  //   goToPricing(navigate);
  // };

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <>
      {/* {upgradePrompt && (
        <div className="plan-prompt" role="status">
          <span className="plan-prompt-label">Note:</span>{' '}
          {upgradePrompt.message}{' '}
          <button type="button" className="plan-prompt-cta" onClick={handleUpgradePromptClick}>
            {upgradePrompt.cta} →
          </button>
        </div>
      )} */}

      <div className="page-head">
        <div>
          <h1>
            Dashboard{' '}
            {isLoading ? <BadgeSkeleton /> : (
              <span className="scope-badge">
                {accountList?.length > 1 && !selectedAccount ? 'ALL ACCOUNTS' : 'SINGLE ACCOUNT'}
              </span>
            )}
          </h1>
          <div className="meta">
            Viewing <b>{currentAccountLabel}</b>
            {lastRefreshed && <> · last refreshed {lastRefreshed.fromNow()}</>}
          </div>
        </div>
        <div className="head-actions">
          {/* Activate Plan — parked for now. Re-enable by uncommenting this
              block together with the usePlanAccess import and the isTrialPlan
              line above; it routes trial users to the pricing page, where the
              trial banner and every plan action already live.
          {isTrialPlan && (
            <button className="btn primary" onClick={() => goToPricing(navigate)}>
              Activate Plan
            </button>
          )}
          */}
          <button
            className="btn ghost"
            onClick={handleRefreshDashboard}
            disabled={isRefreshing || isLoading}
          >
            ↻ {isRefreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button className="btn" onClick={() => navigate(CONST_ROUTE_PATH.ADD_ACCOUNT)}>＋ Add Account</button>

          {/* Date range pill — click opens the MUI DatePicker anchored here */}
          <div
            ref={datePillRef}
            className="date-pill"
            onClick={() => setDatePickerOpen(true)}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <small style={{ color: 'var(--muted)' }}>📅 Check History By Date</small>
            {selectedDate ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <strong>
                  {dataFilters.startMonthDate?.format('DD MMM YYYY')} – {dataFilters.endMonthDate?.format('DD MMM YYYY')}
                </strong>
                <span
                  title="Clear date"
                  onClick={e => { e.stopPropagation(); setSelectedDate(null); }}
                  style={{ fontSize: 13, color: 'var(--muted)', cursor: 'pointer', lineHeight: 1 }}
                >✕</span>
              </span>
            ) : (
              <span style={{ color: 'var(--muted)', fontSize: 13, fontStyle: 'italic' }}>Select a date…</span>
            )}
            <span style={{ color: 'var(--muted)' }}>▾</span>
          </div>

          {/* DatePicker anchored to the pill above — text field hidden, calendar opens in place */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedDate}
              onChange={v => { setSelectedDate(v); setDatePickerOpen(false); }}
              open={datePickerOpen}
              onClose={() => setDatePickerOpen(false)}
              disableFuture
              slotProps={{
                textField: { sx: { display: 'none' } },
                popper: {
                  anchorEl: pillMounted ? datePillRef.current : null,
                  placement: 'bottom-end',
                  modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
                },
              }}
            />
          </LocalizationProvider>
        </div>
      </div>

      {/* ── KPI Tiles ───────────────────────────────────────────── */}
      <div className="kpi-row cols-3">
        <div className="kpi t4">
          <div className="ico"><FaDollarSign /></div>
          <div>
            <div className="lbl">Forecast Cost</div>
            <div className="val">${summary[0].value}K</div>
            <div className="sub">Annual (×{MONTHS_PER_YEAR}) · ${summary[0].annual}K</div>
          </div>
        </div>
        <div className="kpi t1">
          <div className="ico"><FaPiggyBank /></div>
          <div>
            <div className="lbl">Forecast Possible Saving</div>
            <div className="val">${summary[1].value}K</div>
            <div className="sub">Annual (×{MONTHS_PER_YEAR}) · ${summary[1].annual}K</div>
          </div>
        </div>
        <div className="kpi t2">
          <div className="ico"><FaChartBar /></div>
          <div>
            <div className="lbl">% of Forecast Possible Saving</div>
            <div className="val">{summary[2].value}%</div>
            <div className="sub">Across all services</div>
          </div>
        </div>
      </div>

      {/* ── Service Recommendation Cards ─────────────────────────── */}
      {selectedPlatform === PLATFORM.AWS && (
        <div className="svc-grid" style={{ gridTemplateColumns: `repeat(${AWS_SVC_CARDS.length}, 1fr)` }}>
          {AWS_SVC_CARDS.map(({ key, label, costKey }) => {
            const d      = allServicesData[key] || {};
            const cost   = d[costKey] || 0;
            const saving = d.savingCost || 0;
            return (
              <div key={key} className="svc-card">
                <div className="svc-head">{label}</div>
                <div className="svc-body">
                  <div className="svc-title-row">
                    <span className="svc-label">Forecast Possible Savings</span>
                    <span className={`pill${saving === 0 ? ' warn' : ''}`}>${formatValuesInK(saving)}K</span>
                  </div>
                  <div className="chart-sm">
                    <ServiceMiniBarChart cost={cost} saving={saving} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedPlatform === PLATFORM.AZURE && (
        <div className="svc-grid" style={{ gridTemplateColumns: `repeat(${AZURE_SVC_CARDS.length}, 1fr)` }}>
          {AZURE_SVC_CARDS.map(({ key, label, costKey }) => {
            const d      = allServicesData[key] || allServicesData[`${key}S`] || allServicesData[`${key}_SNAPSHOTS`] || {};
            const cost   = d[costKey] || 0;
            const saving = d.savingCost || 0;
            return (
              <div key={key} className="svc-card">
                <div className="svc-head">{label}</div>
                <div className="svc-body">
                  <div className="svc-title-row">
                    <span className="svc-label">Forecast Possible Savings</span>
                    <span className={`pill${saving === 0 ? ' warn' : ''}`}>${formatValuesInK(saving)}K</span>
                  </div>
                  <div className="chart-sm">
                    <ServiceMiniBarChart cost={cost} saving={saving} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Charts Grid (AWS & Azure) ─────────────────────────── */}
      {activeChartCount > 0 && (
        <div className="dash-grid-3" style={{ gridTemplateColumns: `repeat(${activeChartCount}, 1fr)` }}>
          {selectedPlatform === PLATFORM.AWS && savingsByResourceType.labels.length > 0 && (
            <HorizontalBarChart
              labels={savingsByResourceType.labels}
              data={savingsByResourceType.data}
              color={savingsByResourceType.colors}
              title="Unused Resources And Idle Resources"
              subTitle="Savings from unused and idle resources · from Optimizer Recommendations"
            />
          )}
          {costByService.labels.length > 0 && (
            <HorizontalBarChart
              labels={costByService.labels.map(l => shortenLabel(l))}
              fullLabels={costByService.labels}
              data={costByService.data}
              color={CHART_COLORS.savings}
              title="Cost by Service"
              subTitle={`Top ${TOP_SERVICES_LIMIT} services by total cost`}
            />
          )}
          {savingsByRecommendation.labels.length > 0 && (
            <HorizontalBarChart
              labels={savingsByRecommendation.labels}
              data={savingsByRecommendation.data}
              color={CHART_COLORS.unused}
              title="Service wise saving Recommendation"
              subTitle="Potential savings across all services"
            />
          )}
        </div>
      )}
      
      {/* ── Daily Cost Trend (AWS & Azure) ──────────────────────── */}
      {dailyCostTrend.labels.length > 0 && (
        <DailyCostTrendChart
          labels={dailyCostTrend.labels}
          data={dailyCostTrend.data}
          platformLabel={selectedPlatform === PLATFORM.AZURE ? 'Azure' : 'AWS'}
        />
      )}
      
      {/* ── Optimizer Recommendations (AWS) ─────────────────────── */}
      {selectedPlatform === PLATFORM.AWS && (
        <Table
          rows={allOptimizerData}
          columns={allOptimizerColumnList}
          loading={isOptimizerLoading}
          thClassName="svc-head"
          defaultPageSize={10}
          cardClassName="card first"
          headerExtra={<h3 style={{ margin: 0 }}>Optimizer Recommendations</h3>}
          onExport={(rows) => exportCSV(rows, allOptimizerColumnList.filter((c) => typeof c.label === 'string'), buildExportFilename('optimizer_recommendations', selectedAccount))}
          emptyText="No optimizer recommendations found."
          upgradePlanMessage={upgradeMessages['optimizer'] ?? ''}
          subscriptionGated
          ignorePlanLimit
        />
      )}

      {/* ── Azure Advisory Recommendations (Azure) ──────────────── */}
      {selectedPlatform === PLATFORM.AZURE && (
        <Table
          rows={advisoryApiData}
          columns={AZURE_ADVISORY_COLUMNS}
          loading={isLoading}
          thClassName="svc-head"
          defaultPageSize={10}
          cardClassName="card first"
          headerExtra={<h3 style={{ margin: 0 }}>Azure Advisory Recommendations</h3>}
          onExport={(rows) => exportCSV(rows, AZURE_ADVISORY_COLUMNS.filter((c) => typeof c.label === 'string'), buildExportFilename('azure_advisory_recommendations', selectedAccount))}
          emptyText="No Azure advisory recommendations found."
          upgradePlanMessage={upgradeMessages['advisory'] ?? ''}
          subscriptionGated
          ignorePlanLimit
        />
      )}

     

      {/* ── Service-wise Cost Breakup + Recommendations ─────────── */}
      <div className="grid-bottom">
        <div style={{ minWidth: 0, overflow: 'hidden' }}>
          <Table
            rows={costExplorerData}
            columns={costExplorerColumnList}
            loading={isLoading}
            thClassName="svc-head"
            defaultPageSize={10}
            cardClassName="card first"
            headerExtra={<h3 style={{ margin: 0 }}>Service-wise Cost Breakup Trend</h3>}
            onExport={(rows) => exportCSV(rows, costExplorerColumnList.filter((c) => typeof c.label === 'string'), buildExportFilename('service_cost_breakup_trend', selectedAccount))}
            emptyText="No cost data found."
            subscriptionGated
            ignorePlanLimit
          ignorePlanLimit
          />
        </div>

        {/* Recommendations Panel */}
        <Card
          title="Recommendations"
          className="first"
        >
          {hasRecs ? (
            <div className="rec-scroll">
              <PlanGate
                locked={false}
                teaserCount={RECOMMENDATION_TEASER_COUNT}
                items={recommendationItems
                  .map(([key, v]) => {
                    const tagClass = SVC_TAG_CLASS[key] || '';
                    const route = optimizationRouteFor(key);
                    const names = (v.serviceName ?? '').split(',').map(s => s.trim()).filter(Boolean);
                    return (
                      <div key={key} className="rec-item">
                        <div className="rec-item-top">
                          <span className="rec-item-title">{v.displasyAs ?? v.displayAs ?? v.serviceName ?? key}</span>
                          <span className="rec-item-amt">
                            ${(v.savingCost ?? 0).toFixed(2)}
                            {route && (
                              <OpenInNewIcon
                                onClick={() => navigateToOptimization(key)}
                                sx={{ fontSize: 13, ml: 0.5, color: '#2196f3', verticalAlign: 'middle', cursor: 'pointer' }}
                              />
                            )}
                          </span>
                        </div>
                        {names.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                            {names.map((name, ni) => (
                              <span key={ni} className={`tag ${tagClass}`}>{name}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              />
            </div>
          ) : (
            <div className="panel-empty">
              <span className="panel-empty-ico icon-check" aria-hidden="true" />
              <div className="panel-empty-txt">No recommendations at this time</div>
            </div>
          )}

          {/* Plan capped the recommendation list - same note the tables show.
              Rendered through UpgradePlanNote rather than hand-rolled markup:
              this copy hardcoded "Upgrade for full data → Pricing", which sends
              a Standard user to a plan they cannot buy. The component decides
              the wording and destination per plan. */}
          {/* {!isLoading && (
            <UpgradePlanNote
              message={upgradeMessages['allServices']}
              remainingCount={remainingRecommendations}
            />
          )} */}
        </Card>
      </div>

      {/* ── Cost Anomaly (AWS + Azure) ──────────────────────────── */}
      {(selectedPlatform === PLATFORM.AWS || selectedPlatform === PLATFORM.AZURE) && (
        <Table
          rows={anomalyData}
          columns={anomalyColumnList}
          loading={isLoading}
          thClassName="svc-head"
          defaultPageSize={10}
          cardClassName="card first"
          headerExtra={
            <h3 style={{ margin: 0 }}>
              Service wise Cost Anomaly
              {anomalyData.length > 0 && (
                <span className="anom-badge" style={{ marginLeft: 8 }}>⚠ {anomalyData.length} detected</span>
              )}
            </h3>
          }
          onExport={(rows) => exportCSV(rows, anomalyColumnList.filter((c) => typeof c.label === 'string'), buildExportFilename('service_cost_anomaly', selectedAccount))}
          emptyText="No cost anomalies detected."
          upgradePlanMessage={upgradeMessages['anomaly'] ?? ''}
          subscriptionGated
          ignorePlanLimit
        />
      )}

      {/* ── Kubernetes at a glance ──────────────────────────────── */}
      {(isK8sLoading || k8sMetrics?.hasData) && (
      <div style={{ marginTop: 28, paddingTop: 22, borderTop: '1px dashed #e9ecf2' }}>

          {/* Section header */}
          <div className="section-h">
            <div className="ico-circle k8s">⎈</div>
            <h2>Kubernetes — at a glance</h2>
            <span className="s-meta">
              Powered by OpenCost + Prometheus
              {lastK8sRefreshed && <> · refreshed {lastK8sRefreshed.fromNow()}</>} ·&nbsp;
              <button
                className="btn-link"
                onClick={() => navigate(CONST_ROUTE_PATH.K8S.CONNECT_K8S_CLUSTER)}
              >
                Open full K8s dashboard →
              </button>
            </span>
          </div>

          {/* Loading state — show while fetching, hide once data arrives */}
          {isK8sLoading && !k8sMetrics && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 80, color: 'var(--muted)', gap: 10 }}>
              <span className="k8s-spinner" />
              Loading Kubernetes data…
            </div>
          )}

          {/* 4 KPI tiles — only render once we have real data */}
          {k8sMetrics && (
          <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="kpi t2">
              <div className="ico"><FaDollarSign /></div>
              <div>
                <div className="lbl">K8s Total Cost</div>
                <div className="val">${k8sMetrics.totalCost.toFixed(2)}</div>
                <div className="sub">Across {k8sMetrics.activeClusters} clusters</div>
              </div>
            </div>
            <div className="kpi t1">
              <div className="ico"><FaCloud /></div>
              <div>
                <div className="lbl">Active Clusters</div>
                <div className="val">{k8sMetrics.activeClusters}</div>
                <div className="sub">{k8sMetrics.totalNodes} nodes total</div>
              </div>
            </div>
            <div className="kpi t6">
              <div className="ico"><FaExclamationTriangle /></div>
              <div>
                <div className="lbl">Idle Cost</div>
                <div className="val">${k8sMetrics.idleCost.toFixed(2)}</div>
                <div className="sub">
                  {k8sMetrics.totalCost > 0
                    ? `${((k8sMetrics.idleCost / k8sMetrics.totalCost) * 100).toFixed(1)}% of total`
                    : '—'}
                </div>
              </div>
            </div>
            <div className="kpi t3">
              <div className="ico"><FaCogs /></div>
              <div>
                <div className="lbl">Avg Utilization</div>
                <div className="val">{k8sMetrics.avgUtilization != null ? `${k8sMetrics.avgUtilization}%` : '—'}</div>
                <div className="sub">CPU · Mem across all nodes</div>
              </div>
            </div>
          </div>
          )}

          {/* Cost Allocation & Cluster Snapshot — only render once we have real data */}
          {k8sMetrics && (
          <Card title="Cost Allocation & Cluster Snapshot" subtitle="Cost split by resource type · top clusters by spend" className="first">
            <div className="k8s-alloc-grid">

              {/* Donut */}
              <div style={{ position: 'relative', width: 180, height: 180, flexShrink: 0 }}>
                <K8sCostDonutChart
                  nodeCost={k8sMetrics.nodeCost}
                  storageCost={k8sMetrics.storageCost}
                  lbCost={k8sMetrics.lbCost}
                  idleCost={k8sMetrics.idleCost}
                />
                <div className="k8s-donut-center">
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>Total</span>
                  <strong style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.5px' }}>
                    ${k8sMetrics.totalCost.toFixed(2)}
                  </strong>
                </div>
              </div>

              {/* Legend */}
              <div className="k8s-legend">
                {[
                  { color: CHART_COLORS.node,    label: 'Node',          val: k8sMetrics.nodeCost },
                  { color: CHART_COLORS.storage,  label: 'Storage',       val: k8sMetrics.storageCost },
                  { color: CHART_COLORS.lb,       label: 'Load Balancer', val: k8sMetrics.lbCost },
                  { color: CHART_COLORS.idle,     label: 'Idle',          val: k8sMetrics.idleCost },
                ].map(({ color, label, val }) => (
                  <div key={label} className="k8s-legend-row">
                    <span className="k8s-legend-dot" style={{ background: color }} />
                    <span className="k8s-legend-lbl">{label}</span>
                    <span className="k8s-legend-val">${(val ?? 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Top clusters */}
              <div className="k8s-clusters">
                <div className="k8s-clusters-head">Top Clusters</div>
                {(k8sMetrics?.topClusters ?? []).length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--muted)', padding: '8px 0' }}>No cluster data available</div>
                )}
                {(k8sMetrics?.topClusters ?? []).map((c, i) => {
                  const idleAmt = parseFloat(c.clusterIdleCost) || 0;
                  return (
                    <div key={i} className="k8s-cluster-row">
                      <span className="k8s-cluster-ico">☁</span>
                      <div className="k8s-cluster-info">
                        <b>{c.clusterName}</b>
                        <small>
                          {c.region}
                          {c.clusterEfficiency != null ? ` · ${c.clusterEfficiency}% CPU` : ''}
                          {idleAmt > 0 && (
                            <> · <span style={{ color: 'var(--red)' }}>${idleAmt.toFixed(2)} idle</span></>
                          )}
                        </small>
                      </div>
                      <b className="k8s-cluster-cost">${(c.clusterTotalCost ?? 0).toFixed(2)}</b>
                    </div>
                  );
                })}
                <button
                  className="btn"
                  style={{ marginTop: 14, width: '100%', justifyContent: 'center', fontSize: '11.5px' }}
                  onClick={() => navigate(CONST_ROUTE_PATH.K8S.CONNECT_K8S_CLUSTER)}
                >
                  View all clusters →
                </button>
              </div>

            </div>
          </Card>
          )}
        </div>
      )}
    </>
  );
};

export default Dashboard;
